import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { useAuth } from 'src/auth'

import { AgentChatSessions } from './chatSession'

const AgentChatContext = createContext<AgentChatSessions | null>(null)

export const useAgentChatSessions = () => useContext(AgentChatContext)

const OwnedSessions = ({ children }: { children: ReactNode }) => {
  const [sessions] = useState(() => new AgentChatSessions())
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      // React StrictMode immediately reattaches effects. Real teardown still
      // cancels every request, without cancelling a just-started draft in dev.
      queueMicrotask(() => {
        if (!mounted.current) sessions.dispose()
      })
    }
  }, [sessions])

  return (
    <AgentChatContext.Provider value={sessions}>
      {children}
    </AgentChatContext.Provider>
  )
}

/** Lives above route/layout switches, but never shares responses across
 * authenticated users. Signing out cancels outstanding requests. */
export const AgentChatProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useAuth()
  const id = currentUser?.id
  const owner =
    typeof id === 'number' || typeof id === 'string' ? id : 'signed-out'
  return <OwnedSessions key={owner}>{children}</OwnedSessions>
}
